import firebase from "./firebase";
import "firebase/compat/auth";
import { ROOT_ROUTE } from "../routes";
import { clearPendingEmailChange } from "./pendingEmailChange";
import { storeFirebaseToken } from "./firebaseSession";
import { getStorage } from "./storage";
import { appVersion } from "../appVersion";
import { clearPendingEmailSignIn } from './pendingEmailSignIn';

export { getStorage } from "./storage";

export { appVersion } from "../appVersion";

export const setFirebasePersistence = (
  rememberMe = Boolean(localStorage.getItem("rememberMe")),
  auth = firebase.auth()
) => {
  const persistence = firebase.auth.Auth?.Persistence || {};
  return auth.setPersistence(
    rememberMe ? persistence.LOCAL || "local" : persistence.SESSION || "session"
  );
};

/**
 * Return if user is logged in
 * This is completely up to you and how you want to store the token in your frontend application
 * e.g. If you are using cookies to store the application please update this function
 */
// eslint-disable-next-line arrow-body-style
export const isUserLoggedIn = async () => {
  const user = getStorage().getItem("user");
  const token = getStorage().getItem("token");
  if (user && token) {
    try {
      const currentUser = firebase.auth().currentUser;
      return !!currentUser;
    } catch (error) {
      console.error("Error verifying login status:", error);
      return false;
    }
  }
  return false;
};

export const hasUserVisibility = (routeRoles) => {
  if (!routeRoles) {
    return true;
  }

  if (!isUserLoggedIn()) {
    return false;
  }

  const user = JSON.parse(getStorage().getItem("user"));
  const userRoles = user?.roles;

  if (!userRoles.length) {
    return false;
  }

  return Boolean(
    userRoles.find((userRole) => {
      return Boolean(routeRoles.find((role) => role === userRole));
    })
  );
};
// export const getUserData = () => JSON.parse(localStorage.getItem("userData"));
export const getUserData = () =>
  getStorage().getItem("user")
    ? JSON.parse(getStorage().getItem("user"))
    : null;

/**
 * This function is used for demo purpose route navigation
 * In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 * Please note role field is just for showing purpose it's not used by anything in frontend
 * We are checking role just for ease
 * NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole) => {
  if (userRole === "admin") return ROOT_ROUTE;
  if (userRole === "client") return { name: "access-control" };
  return { name: "auth-login" };
};

export const signOut = () => {
  clearPendingEmailSignIn();
  clearPendingEmailChange();
  getStorage().removeItem("pendingMobileChange");
  return firebase
    .auth()
    .signOut()
    .then((confirmationResult) => {
      console.log("Successfully signed out");
      return confirmationResult;
    })
    .catch((error) => {
      console.error("Error signing out:", error);
      throw new Error("Unable to sign out");
    });
};

function setUpRecaptcha(host) {
  if (!host?.isConnected) throw new Error("Phone login is no longer available");
  // Invisible reCAPTCHA renders directly into its target. Give each send its
  // own element so retries never render into an already-used widget target.
  const target = document.createElement("div");
  host.appendChild(target);
  try {
    const verifier = new firebase.auth.RecaptchaVerifier(target, {
      size: "invisible",
      callback: () => {
        console.log("reCAPTCHA resolved");
      },
      "expired-callback": () => {
        console.log("reCAPTCHA expired");
      },
    });
    return {
      verifier,
      clear: () => {
        try { verifier.clear(); }
        catch (error) { console.warn("Error clearing reCAPTCHA:", error.message); }
        finally { target.remove(); }
      },
    };
  } catch (error) {
    target.remove();
    console.error("Error setting up reCAPTCHA:", error);
    throw error;
  }
}

export const signIn = async (mobile) => {
  const host = document.getElementById("recaptcha-container");
  const auth = firebase.auth();
  await setFirebasePersistence(
    Boolean(localStorage.getItem("rememberMe")),
    auth
  );
  const recaptcha = setUpRecaptcha(host);
  try {
    // Firebase resets the widget before this promise settles. Clear it only
    // afterward; the returned confirmation result does not need the verifier.
    return await auth.signInWithPhoneNumber(mobile, recaptcha.verifier);
  } catch (error) {
    console.error("SMS not sent:", error.message);
    throw new Error("Unable to send verification SMS. Please try again.");
  } finally {
    recaptcha.clear();
  }
};

export const verificationCode = (confirmationResult, code) => {
  const auth = firebase.auth();

  return setFirebasePersistence(
    Boolean(localStorage.getItem("rememberMe")),
    auth
  )
    .then(() => confirmationResult.confirm(code))
    .then(async (result) => {
      // User signed in successfully.
      const user = result.user;
      const token = await user?.getIdToken(true);
      return storeFirebaseToken(user, token);
    })
    .catch((error) => {
      console.error("Invalid verification code:", error.message);
      throw new Error("Invalid code. Please try again.");
    });
};

export const getFirebaseUser = () => {
  return new Promise((resolve, reject) => {
    firebase.auth().onAuthStateChanged(function (user) {
      if (user && Object.keys(user).length > 0) {
        process.env.REACT_APP_NODE_ENV === "development" &&
          console.log(
            "[utils.js - getFirebaseUser] user => ",
            JSON.stringify(user)
          );
        resolve(user);
      } else {
        reject();
      }
    });
  });
};

export const clearCookies = () => {
  document.cookie.split(";").forEach((c) => {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
};

export const clearStorages = (appKeys = ["user", "token"]) => {
  // Remove only app-specific keys from localStorage
  appKeys.forEach((key) => localStorage.removeItem(key));
  appKeys.forEach((key) => sessionStorage.removeItem(key));
};

export const checkAppVersion = async () => {
  const savedVersion = localStorage.getItem("appVersion");
  const currentVersion = appVersion;

  if (savedVersion !== currentVersion) {
    // A release version change does not invalidate Firebase sessions or pending
    // account-bound actions. Session bootstrap still verifies Firebase and Core.
    localStorage.setItem("appVersion", currentVersion);
  }
};
